import React, { useEffect, useRef, useState } from 'react';
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonPage, IonRefresher, IonRefresherContent, IonSpinner, IonTitle, IonToolbar, useIonViewDidEnter, useIonViewWillLeave } from '@ionic/react';
import { arrowBackOutline, refreshOutline } from 'ionicons/icons';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { listaOperacaoValida, requestService } from '../../../../../Utils/Services';
import { OperacoesValidaResponse, validarOperacoesValida } from './operacaoValida';
import './MonitorValida.css';

const ionicProps = { placeholder: undefined, onPointerEnterCapture: undefined, onPointerLeaveCapture: undefined };
const colunas = ['Nota fiscal', 'Situacao', 'Origem', 'Localizacao', 'Inicio', 'Duracao'];
const texto = (valor: unknown) => typeof valor === 'string' && valor.trim() ? valor : '-';

const MonitorValida: React.FC = () => {
  const history = useHistory();
  const [dados, setDados] = useState<OperacoesValidaResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const consultaPendente = useRef<AbortController | null>(null);
  const ativa = useRef(false);
  const visita = useRef(0);
  const intervalo = useRef<ReturnType<typeof setInterval> | null>(null);

  async function carregar(completar?: () => void) {
    if (!ativa.current || consultaPendente.current) {
      completar?.();
      return;
    }
    const controller = new AbortController();
    const numeroVisita = visita.current;
    const visitaAtual = () => ativa.current && visita.current === numeroVisita;
    consultaPendente.current = controller;
    setCarregando(true);
    try {
      await requestService(listaOperacaoValida.url, {
        method: listaOperacaoValida.method,
        headers: [['token', localStorage.getItem('token') || '']],
        signal: controller.signal
      }, { replace: (path: string) => { if (visitaAtual()) history.replace(path); } }, (payload: any) => {
        if (!visitaAtual()) return;
        setDados(validarOperacoesValida(payload));
        setErro('');
      }, () => {
        if (visitaAtual()) setErro('Nao foi possivel carregar as operacoes Valida.');
      });
    } catch {
      if (visitaAtual()) setErro('Nao foi possivel carregar as operacoes Valida.');
    } finally {
      if (visitaAtual() && consultaPendente.current === controller) {
        consultaPendente.current = null;
        setCarregando(false);
      }
      completar?.();
    }
  }

  function encerrarVisita() {
    ativa.current = false;
    visita.current += 1;
    if (intervalo.current !== null) clearInterval(intervalo.current);
    intervalo.current = null;
    consultaPendente.current?.abort();
    consultaPendente.current = null;
  }

  useIonViewDidEnter(() => {
    encerrarVisita();
    ativa.current = true;
    void carregar();
    intervalo.current = setInterval(() => { void carregar(); }, 30000);
  });
  useIonViewWillLeave(encerrarVisita);
  useEffect(() => encerrarVisita, []);

  return (
    <IonPage className="monitor-valida" {...ionicProps}>
      <IonHeader {...ionicProps}>
        <IonToolbar color="primary" {...ionicProps}>
          <IonButtons slot="start" {...ionicProps}>
            <IonButton aria-label="Voltar ao Perfil" title="Voltar ao Perfil" onClick={() => history.push('/cliente/conta')} {...ionicProps}>
              <IonIcon slot="icon-only" icon={arrowBackOutline} {...ionicProps} />
            </IonButton>
          </IonButtons>
          <IonTitle {...ionicProps}>Monitor Valida</IonTitle>
          <IonButtons slot="end" {...ionicProps}>
            <IonButton aria-label="Atualizar operacoes" title="Atualizar operacoes" disabled={carregando} onClick={() => { void carregar(); }} {...ionicProps}>
              <IonIcon slot="icon-only" icon={refreshOutline} {...ionicProps} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent {...ionicProps}>
        <IonRefresher slot="fixed" onIonRefresh={event => { void carregar(() => { void event.detail.complete(); }); }} {...ionicProps}>
          <IonRefresherContent {...ionicProps} />
        </IonRefresher>
        <main className="monitor-valida-conteudo">
        {carregando && <div className="monitor-valida-atividade" role="status" aria-live="polite">
          <IonSpinner name="crescent" {...ionicProps} />
          <span>{dados ? 'Atualizando operacoes...' : 'Carregando operacoes...'}</span>
        </div>}
        {erro && <div className="monitor-valida-erro" role="alert">
          <p>{dados ? 'Nao foi possivel atualizar. Os dados exibidos sao da ultima consulta.' : erro}</p>
          <IonButton disabled={carregando} onClick={() => { void carregar(); }} {...ionicProps}>
            <IonIcon slot="start" icon={refreshOutline} {...ionicProps} />
            Tentar novamente
          </IonButton>
        </div>}
        {dados && <p className="monitor-valida-total">Operacoes em andamento: {dados.Count}</p>}
        {dados && dados.Count === 0 && !erro && <p className="monitor-valida-vazio">Nao ha operacoes Valida em andamento.</p>}
        {dados && dados.Count > 0 && <table aria-label="Operacoes Valida" className="monitor-valida-lista">
          <thead><tr>{colunas.map(coluna => <th key={coluna} scope="col">{coluna}</th>)}</tr></thead>
          <tbody>{dados.Items.map((operacao, indice) => {
            const notas = Array.isArray(operacao.notaFiscal)
              ? operacao.notaFiscal.filter(nota => typeof nota === 'string' && nota.trim()) : [];
            const inicio = typeof operacao.dataInicio === 'string' && operacao.dataInicio.trim()
              ? dayjs(operacao.dataInicio) : null;
            const campos = [
              notas.length ? notas.join(', ') : texto(operacao.id),
              texto(operacao.status), texto(operacao.origem), texto(operacao.localizacao),
              inicio?.isValid() ? inicio.format('DD/MM/YYYY HH:mm') : '-',
              inicio?.isValid() ? `${Math.max(0, dayjs().diff(inicio, 'minute'))} min` : '-'
            ];
            return <tr key={texto(operacao.pk) !== '-' ? operacao.pk : `${texto(operacao.id)}-${indice}`}>
              {campos.map((campo, coluna) => <td key={colunas[coluna]}>
                <span className="monitor-valida-campo">{colunas[coluna]}</span>
                <span className={coluna === 1 ? 'monitor-valida-situacao' : undefined}>{campo}</span>
              </td>)}
            </tr>;
          })}</tbody>
        </table>}
        </main>
      </IonContent>
    </IonPage>
  );
};

export default MonitorValida;